<template>
	<div class="maku-container">
		<el-container>
			<el-container>
				<el-header class="maku-header">
					<Header
						v-bind="$props"
						@import="handleImport"
						@preview="() => (previewVisible = true)"
						@clearable="handleClearable"
						@generate-json="handleGenerateJson"
					>
						<template #button>
							<slot name="button"></slot>
						</template>
					</Header>
				</el-header>
				<el-container>
					<el-aside class="maku-sidebar">
						<Sidebar />
					</el-aside>
					<el-main class="maku-main">
						<WidgetForm v-model:widgetForm="widgetForm" v-model:activeForm="activeForm" />
					</el-main>
				</el-container>
			</el-container>
			<el-aside class="maku-config">
				<el-tabs v-model="activeTab" stretch class="maku-tabs">
					<el-scrollbar height="calc(100vh - 42px)">
						<el-tab-pane :label="t('designForm.fieldSettings')" name="formItem">
							<FormItemConfig :key="activeForm?.key" v-model:active="activeForm" />
						</el-tab-pane>
						<el-tab-pane :label="t('designForm.formConfig')" name="form">
							<FormConfig v-model:config="widgetForm.config" />
						</el-tab-pane>
					</el-scrollbar>
				</el-tabs>
			</el-aside>
		</el-container>
		<el-dialog v-model="importVisible" :title="t('designForm.import')" center draggable :width="800">
			<ma-code-editor v-if="importVisible" v-model="importData" language="json" />
			<template #footer>
				<el-button type="primary" @click="handleImportJson">{{ t('designForm.import') }}</el-button>
				<el-button @click="importVisible = false">{{ t('designForm.cancel') }}</el-button>
			</template>
		</el-dialog>

		<el-dialog v-model="previewVisible" :title="t('designForm.preview')" center draggable :width="800">
			<GenerateForm v-if="previewVisible" ref="generateFormRef" :upload="upload" :data="widgetForm" />
			<template #footer>
				<el-button type="primary" @click="handleFormJsonData">{{ t('designForm.generateCode') }}</el-button>
				<el-button @click="handleReset">{{ t('designForm.reset') }}</el-button>
				<el-button @click="previewVisible = false">{{ t('designForm.cancel') }}</el-button>
			</template>

			<el-dialog v-model="formJsonVisible" :title="t('designForm.generateCode')" center :width="800">
				<ma-code-editor v-if="formJsonVisible" v-model="formJsonData" language="json" readonly />

				<template #footer>
					<el-button type="primary" @click="handleFormCopy">{{ t('designForm.copy') }}</el-button>
					<el-button @click="() => (formJsonVisible = false)">{{ t('designForm.cancel') }}</el-button>
				</template>
			</el-dialog>
		</el-dialog>

		<el-dialog v-model="generateJsonVisible" :title="t('designForm.generateCode')" center draggable :close-on-click-modal="false" :width="800">
			<ma-code-editor v-if="generateJsonVisible" v-model="generateJsonData" language="json" readonly />
			<template #footer>
				<el-button type="primary" @click="handleJsonCopy">{{ t('designForm.copy') }}</el-button>
			</template>
		</el-dialog>
	</div>
</template>
<script lang="ts">
export default {
	name: 'DesignForm'
}
</script>
<script lang="ts" setup>
import { PropType, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import useClipboard from 'vue-clipboard3'
import Header from './Header.vue'
import Sidebar from './sidebar/index.vue'
import WidgetForm from './WidgetForm.vue'
import FormConfig from './FormConfig.vue'
import FormItemConfig from './form-item-config/index.vue'
import MaCodeEditor from '@/components/ma-code-editor/index.vue'
import GenerateForm from '../generate-form/GenerateForm.vue'
import { defaultWidgetFormData, IWidgetForm } from '@/components/design-form/config'
import { ElMessage } from 'element-plus'

const { t } = useI18n()
const { toClipboard } = useClipboard()
// 显示字段配置
const activeTab = ref('formItem')

const props = defineProps({
	data: {
		type: Object as PropType<IWidgetForm>,
		required: false,
		default: () => ref<IWidgetForm>(JSON.parse(JSON.stringify(defaultWidgetFormData)))
	},
	import: {
		type: Boolean,
		default: true
	},
	preview: {
		type: Boolean,
		default: true
	},
	clearable: {
		type: Boolean,
		default: true
	},
	generateJson: {
		type: Boolean,
		default: true
	},
	upload: {
		type: String,
		default: ''
	}
})

watch(
	() => props.data,
	val => {
		widgetForm.value = JSON.parse(JSON.stringify(val))
		if (widgetForm.value.list) {
			activeForm.value = widgetForm.value.list[0]
		}
	}
)

const widgetForm = ref<IWidgetForm>(props.data)
const activeForm = ref()

const generateFormRef = ref()

const importVisible = ref(false)
const previewVisible = ref(false)
const formJsonVisible = ref(false)
const generateJsonVisible = ref(false)

const importData = ref()
const handleImport = () => {
	importData.value = JSON.stringify(defaultWidgetFormData, null, 2)
	importVisible.value = true
}
const handleImportJson = () => {
	try {
		widgetForm.value = JSON.parse(importData.value)

		if (widgetForm.value.list) {
			activeForm.value = widgetForm.value.list[0]
		}

		importVisible.value = false
	} catch (error) {
		ElMessage.error('导入失败，请检查JSON数据')
	}
}

const formJsonData = ref()
const handleFormJsonData = () => {
	generateFormRef.value.getData().then((res: any) => {
		formJsonData.value = JSON.stringify(res, null, 2)
		formJsonVisible.value = true
	})
}

const handleFormCopy = () => {
	toClipboard(formJsonData.value)
	ElMessage.success('复制成功')
}

const handleReset = () => {
	generateFormRef.value.reset()
}

const handleClearable = () => {
	widgetForm.value = JSON.parse(JSON.stringify(defaultWidgetFormData))
	activeForm.value = null
}

const generateJsonData = ref()
const handleGenerateJson = () => {
	generateJsonData.value = JSON.stringify(widgetForm.value, null, 2)

	generateJsonVisible.value = true
}

const handleJsonCopy = () => {
	toClipboard(JSON.stringify(widgetForm.value, null, 2))
	ElMessage.success('复制成功')
}

const getFormJson = () => {
	return JSON.stringify(widgetForm.value, null, 2)
}

defineExpose({
	getFormJson
})
</script>

<style lang="scss" scoped>
.maku-container {
	width: 100%;
	height: 100%;
	::v-deep(.el-main) {
		padding: 2px !important;
	}
}
.maku-sidebar {
	width: 250px;
	height: 100vh;
	border-right: #eee solid 1px;
}
.maku-header {
	height: 42px;
	line-height: 42px;
	padding-left: 10px;
	border-bottom: #eee solid 2px;
}
.maku-main {
	border: #999 dashed 1px;
	margin: 10px;
	height: calc(100vh - 60px);
}
.maku-config {
	width: 300px;
	border-left: #eee solid 1px;
}

.maku-tabs {
	::v-deep(.el-tabs__item) {
		font-size: 14px !important;
		height: 42px;
	}

	::v-deep(.el-tab-pane) {
		padding: 0 10px;
	}
}
</style>
